import {
	ReactElement,
	Fragment,
	useEffect,
	RefObject,
	useState,
	useRef,
	useCallback,
	useMemo,
	MouseEvent,
	ReadonlyRefObject
} from "react";
import { ActionAndOthers, ActionKind } from "client_legacy/ndaw/actions";
import { Coordinate } from "client/Misc/commonTypes";
import { DropOverActionList } from "../DropOverActionList/DropOverActionList";
import { actionComponentFactory, computeActionsToRender } from "../ActionList/actionsRenderHelpers";
import { useStableImperativeCallback } from "client/Hooks";
import { resolveFormattedTextString } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";

export type ContextMenuProps<TContext> = {
	actions: readonly ActionAndOthers<TContext>[];
	context: TContext;
	associatedElement: HTMLElement;
	position: Coordinate;
	onClose(): void;
	trackingId: string;
	title: string;
};

export const ContextMenu = <TContext,>(
	props: ContextMenuProps<TContext>
): ReactElement => {
	const {
		actions,
		context,
		associatedElement,
		position,
		onClose,
		trackingId,
		title
	} = props;

	const actionsToRender: ActionAndOthers<TContext>[] = computeActionsToRender(
		actions.filter(x => x.kind !== ActionKind.Divider), // "manual" dividers are not rendered, the dividers get automatically injected
		context,
		true
	);

	const stableOnClose: typeof onClose = useStableImperativeCallback(onClose);

	useEffect(
		(): void => {
			if (actionsToRender.length === 0) {
				stableOnClose();
			}
		},
		[actionsToRender.length, stableOnClose]
	);

	return (
		<DropOverActionList
			trackingId={trackingId}
			target={associatedElement}
			onClose={onClose}
			targetAlignment={{ kind: "absolute", coordinate: position }}
			bubbleClick={false}
			closeOnMouseDown
			menuTitle={title}
		>
			{actionsToRender.map((x) => (
				<Fragment key={x.name}>
					{actionComponentFactory(x, context)}
				</Fragment>
			))}
		</DropOverActionList>
	);
};
ContextMenu.displayName = "ContextMenu";

export function computeContextMenuTitle<TItem>(
	selection: readonly TItem[],
	nameSelector: (item: TItem) => string
): string {
	switch (selection.length) {
		case 0:
			return "";
		case 1:
			return nameSelector(selection[0]);
		default:
			return resolveFormattedTextString({
				template: localize("_AmountItemsSelected"),
				replacements: [{
					what: "{{Amount}}",
					filler: selection.length
						.toString()
				}]
			});
	}
}

export type ContextMenuPropsProvidedByHook = Pick<ContextMenuProps<unknown>, "associatedElement" | "position" | "onClose">;

export function useContextMenu<TElement extends HTMLElement>(
	associatedElementRef: ReadonlyRefObject<TElement | null>
): [
	(args: MouseEvent | Coordinate) => void,
	ContextMenuPropsProvidedByHook | undefined
] {
	const [position, setPosition] = useState<Coordinate | undefined>(undefined);
	const propsVersion: RefObject<number> = useRef<number>(0);

	const contextMenuHandler = useCallback(
		(args: MouseEvent | Coordinate): void => {
			let coordinate: Coordinate;
			if (isMouseEvent(args)) {
				args.preventDefault();
				coordinate = {
					x: args.clientX,
					y: args.clientY
				};
			} else {
				coordinate = args;
			}

			setPosition(coordinate);
		},
		[]
	);

	const propsForContextMenu = useMemo(
		(): ContextMenuPropsProvidedByHook | undefined => {
			const associatedElement: HTMLElement | null = associatedElementRef.current;
			if (!(associatedElement && position)) {
				return undefined;
			}

			const issuedPropsVersion: number = ++propsVersion.current;
			return {
				position,
				associatedElement,
				onClose: () => {
					const isStale: boolean = issuedPropsVersion !== propsVersion.current;
					if (isStale) {
						return;
					}
					setPosition(undefined);
				}
			};
		},
		[position, associatedElementRef]
	);

	return [
		contextMenuHandler,
		propsForContextMenu
	];
}

function isMouseEvent(value: MouseEvent | Coordinate): value is MouseEvent {
	return Boolean((value as MouseEvent).preventDefault);
}